/**
 * ZizhanDialog：紫占起盘弹窗（命例页「紫占」按钮入口）
 * 选项序（上→下，每项独占一行）：当前时刻起盘男 → 当前时刻起盘女 → 系统随机（自动）→ 系统随机排盘（报数）（左按钮 2/3，需右侧输入 1–999 方可点击）→ 当前时刻限流盘男 → 当前时刻限流盘女
 * - 「保存到命例库」开关默认关：关 = 仅展示不入库（transientCase 兜底中宫显示）；开 = 写入命例库
 * - 起盘成功跳 #/chart；失败保留弹窗展示 store.error
 */
import { useState } from 'react';
import { useChartStore } from '../state/store';
import type { ZizhanRequest } from '@shared/types/api';

export default function ZizhanDialog({ onClose }: { onClose: () => void }) {
  const zizhanChart = useChartStore((s) => s.zizhanChart);
  const loading = useChartStore((s) => s.loading);
  const [numStr, setNumStr] = useState('');
  const [numErr, setNumErr] = useState('');
  const [persist, setPersist] = useState(false);
  const [localErr, setLocalErr] = useState('');

  async function go(req: ZizhanRequest, name: string, sixLayer?: boolean) {
    setLocalErr('');
    await zizhanChart(req, { persist, name, sixLayer });
    const err = useChartStore.getState().error;
    if (err) {
      setLocalErr(err);
      return;
    }
    onClose();
    window.location.hash = '#/chart';
  }

  function onNumber() {
    const n = Number(numStr.trim());
    if (!Number.isInteger(n) || n < 1 || n > 999) {
      setNumErr('报数须为 1–999 整数');
      return;
    }
    setNumErr('');
    void go({ mode: 'number', number: n }, `紫占·报数${n}`);
  }

  const subCls = 'rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40';

  // 报数是否合法（1–999 整数）：未填或非法时「系统随机排盘（报数）」按钮禁用
  const numTrim = numStr.trim();
  const numParsed = Number(numTrim);
  const numValid = numTrim !== '' && Number.isInteger(numParsed) && numParsed >= 1 && numParsed <= 999;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-72 rounded-xl bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-medium text-slate-800">紫占起盘</span>
          <button type="button" onClick={onClose} className="rounded px-2 py-0.5 text-slate-400 hover:bg-slate-100">
            ✕
          </button>
        </div>

        <div className="flex flex-col gap-2">
          <button type="button" disabled={loading} className={subCls} onClick={() => void go({ mode: 'now', gender: 'male' }, '紫占·当前时刻男')}>
            当前时刻起盘（男盘）
          </button>
          <button type="button" disabled={loading} className={subCls} onClick={() => void go({ mode: 'now', gender: 'female' }, '紫占·当前时刻女')}>
            当前时刻起盘（女盘）
          </button>

          <button type="button" disabled={loading} className={subCls} onClick={() => void go({ mode: 'random' }, '紫占·随机')}>
            系统随机起盘（自动）
          </button>

          <div className="grid grid-cols-3 items-center gap-2">
            <button type="button" disabled={loading || !numValid} className={`${subCls} col-span-2`} onClick={onNumber}>
              系统随机排盘（报数）
            </button>
            <input
              value={numStr}
              onChange={(e) => setNumStr(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onNumber(); }}
              placeholder="1–999"
              inputMode="numeric"
              className="col-span-1 min-w-0 rounded-md border border-slate-300 px-2 py-2 text-sm outline-none focus:border-slate-500"
            />
          </div>
          {numErr && <div className="text-xs text-red-600">{numErr}</div>}

          <button type="button" disabled={loading} className={subCls} onClick={() => void go({ mode: 'now', gender: 'male' }, '紫占·限流男', true)}>
            当前时刻限流盘（男盘）
          </button>
          <button type="button" disabled={loading} className={subCls} onClick={() => void go({ mode: 'now', gender: 'female' }, '紫占·限流女', true)}>
            当前时刻限流盘（女盘）
          </button>
        </div>

        <label className="mt-4 flex cursor-pointer items-center justify-between text-xs text-slate-600">
          <span>保存到命例库</span>
          <button
            type="button"
            onClick={() => setPersist((v) => !v)}
            className={`relative h-5 w-9 rounded-full transition-colors ${persist ? 'bg-slate-700' : 'bg-slate-300'}`}
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${persist ? 'left-4.5' : 'left-0.5'}`}
            />
          </button>
        </label>

        {(localErr || loading) && (
          <div className={`mt-3 text-xs ${localErr ? 'text-red-600' : 'text-slate-400'}`}>
            {localErr || '起盘中…'}
          </div>
        )}
      </div>
    </div>
  );
}
